import { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { BottomSheet, useFocusEffect } from '@betty/navigation';
import { Box, ScrollView, useLayout } from '@betty/moonlight';
import { DepositPromoId } from '../../state/deposit/deposit.types';
import { getAmountLimit, isAmountLimitReached } from './Amounts/Amounts.utils';
import {
  getDefaultAmount,
  getDefaultPromos,
  getAvailablePromos,
  getPromoQualificationAmount,
} from './Deposit.utils';
import useDepositPromoHandler from '../../hooks/useDepositPromoHandler';
import useDepositPromos from '../../hooks/useDepositPromos';
import useDepositSettings from '../../hooks/useDepositSettings';
import AmountsSkeleton from './Amounts/AmountsSkeleton';
import PromosSkeleton from './Promos/PromosSkeleton';
import Amounts from './Amounts/Amounts';
import Promos from './Promos/Promos';
import Footer from './Footer/Footer';

function Deposit() {
  const { data: promos, fetch: fetchPromos } = useDepositPromos();
  const { data: settings, fetch: fetchSettings } = useDepositSettings();
  const [footerLayout, onFooterLayout] = useLayout();
  const [selectedAmount, setSelectedAmount] = useState<number>();
  const [selectedPromoIds, setSelectedPromoIds] = useState<DepositPromoId[]>(
    [],
  );
  const limit = useMemo(
    () => settings && getAmountLimit(settings.limits),
    [settings],
  );
  const limitReached = isAmountLimitReached(limit);
  const { loading, handleDepositPromo } = useDepositPromoHandler(
    promos,
    selectedAmount,
    selectedPromoIds,
  );
  const fetching = !promos || !settings;

  const onSelectOtherAmount = useCallback(
    (amount?: number) => {
      BottomSheet.open({
        namespace: 'wallet',
        type: 'DepositAmount',
        data: {
          amount,
          minAmount: settings?.minAmount,
          maxAmount: settings?.maxAmount,
          limits: settings?.limits,
          onSelect: setSelectedAmount,
        },
      });
    },
    [settings],
  );

  const onSelectPromo = useCallback((id: DepositPromoId) => {
    setSelectedPromoIds([id]);
  }, []);

  const onSelectNoPromo = useCallback(() => {
    setSelectedPromoIds([]);
  }, []);

  useFocusEffect(
    useCallback(() => {
      const fetch = async () => {
        const promos = await fetchPromos();
        const settings = await fetchSettings(
          getPromoQualificationAmount(promos),
        );

        setSelectedAmount((amount) => getDefaultAmount(settings, amount));

        setSelectedPromoIds((prevIds) => {
          const allSelectedExist =
            prevIds.length > 0 &&
            prevIds.every((id) =>
              promos.some((promo) => promo.promotionId === id),
            );

          return allSelectedExist ? prevIds : getDefaultPromos(promos);
        });
      };

      fetch();
    }, [fetchPromos, fetchSettings]),
  );

  useEffect(() => {
    setSelectedPromoIds((selectedIds) =>
      getAvailablePromos(selectedIds, promos, selectedAmount),
    );
  }, [selectedAmount, promos]);

  return (
    <Box flex={1}>
      <ScrollView
        contentContainerStyle={{
          gap: 'space-600',
          paddingTop: 'space-400',
          paddingHorizontal: 'space-400',
        }}
      >
        {!fetching ? (
          <Amounts
            selectedAmount={selectedAmount}
            onSelectAmount={setSelectedAmount}
            onSelectOtherAmount={onSelectOtherAmount}
            {...settings}
          />
        ) : (
          <AmountsSkeleton />
        )}
        {!fetching ? (
          <Promos
            promos={promos}
            limitReached={limitReached}
            selectedAmount={selectedAmount}
            selectedPromoIds={selectedPromoIds}
            onSelectPromo={onSelectPromo}
            onSelectNoPromo={onSelectNoPromo}
          />
        ) : (
          <PromosSkeleton />
        )}
        <Box height={footerLayout.height} />
      </ScrollView>
      {!fetching ? (
        <Footer
          promos={promos}
          loading={loading}
          disabled={limitReached || !selectedAmount}
          selectedAmount={selectedAmount}
          selectedPromoIds={selectedPromoIds}
          onLayout={onFooterLayout}
          onButtonPress={handleDepositPromo}
        />
      ) : null}
    </Box>
  );
}

export default memo(Deposit);
